import { api } from '../api/client';

import { API_SAVED_FILTERS_URL } from '../utils';
import { withElementChoicesField } from '../utils/elementRefusal';
import { sortThenPayload } from '../utils/sortLevels';
import { savedColumnsOf, withSavedFilterView, SAVED_FILTER_VIEWS } from '../utils/savedFilterView';

/**
 * Saved KQL filters for one collection, in the same two tiers as favorites:
 * `scope: 'user'` (personal, creator-only) and `scope: 'collection'` (pinned
 * for everyone, manager-managed).
 *
 * The server canonicalizes every stored query, so what comes back is the text
 * to put in `?q=` verbatim. Denials follow the 404/403 line in
 * keepr-api docs/SCHEMA.md — someone else's user-tier filter simply does not
 * exist as far as this client is concerned, which is why the UI only ever
 * offers actions the caller's role already covers.
 */

// A board's names and nothing else (`{ by, lanes? }`): the API refuses any other key.
const boardPayload = board => (board.lanes ? { by: board.by, lanes: board.lanes } : { by: board.by });

const filterUrl = (collectionId, filterId) =>
	`${API_SAVED_FILTERS_URL(collectionId)}/${filterId}`;

/** Collection tier first, then the caller's own, name-sorted within each. */
export async function getSavedFilters(collectionId) {
	return (await getSavedFilterLists(collectionId)).filters;
}

/**
 * The collection's own views. A sub-collection inherits none from the
 * collections above it (owner ruling 2026-10-21, KPR-1084): the API's
 * `inherited` is always empty and is not read.
 */
export async function getSavedFilterLists(collectionId) {
	const { data } = await api.get(API_SAVED_FILTERS_URL(collectionId));
	return { filters: Array.isArray(data?.filters) ? data.filters.map(asRead) : [] };
}

/**
 * A filter as the client reads it: `sort_then` (its sort levels 2 and 3,
 * KPR-1636) is always a list or null, so an API from before the levels (which
 * sends no key) reads as one level, like a filter saved before them.
 */
export const withSortThen = filter => (filter && typeof filter === 'object'
	? { ...filter, sort_then: sortThenPayload(filter.sort_then) }
	: filter);

/** Both readings: the sort levels, and what it opens in (KPR-1641: card, columns, view, null when unset). */
const asRead = filter => withSavedFilterView(withSortThen(filter));

// What it opens in (KPR-1641), as the API takes it: a card id, saved columns
// (`#id` / system), a view mode. `null` for none.
const viewPayload = (key, value) => {
	if (key === 'card') return typeof value === 'string' && value ? value : null;
	if (key === 'view') return SAVED_FILTER_VIEWS.includes(value) ? value : null;
	const columns = savedColumnsOf(value);
	return columns.length ? columns : null;
};
const VIEW_KEYS = ['card', 'columns', 'view'];

/**
 * Always creates in the USER tier — promotion is a separate, manage-gated step.
 *
 * `group_by` is the grid's separator period (utils/itemGroups tokens). The API
 * refuses an empty string for it — only `null` clears one — so an absent
 * period is simply left out of the payload, never sent as ''.
 */
export async function createSavedFilter(collectionId, { name, query, sort_field, sort_direction, sort_then, group_by, board, elementChoices, ...view }) {
	const payload = { name, query };
	if (sort_field) payload.sort_field = sort_field;
	if (sort_direction) payload.sort_direction = sort_direction;
	// "Then by" (KPR-1636): levels 2 and 3. None is left out (the API stores nothing).
	const levels = sortThenPayload(sort_then);
	if (levels) payload.sort_then = levels;
	if (group_by) payload.group_by = group_by;
	// The board on screen (KPR-1395): `{ by, lanes? }`, element names. A
	// filter saved from another view stores none.
	if (board?.by) payload.board = boardPayload(board);
	// What it opens in (KPR-1641): each left out when there is none.
	VIEW_KEYS.forEach((key) => {
		const value = viewPayload(key, view[key]);
		if (value) payload[key] = value;
	});
	// The person's answer to an ambiguous element name (utils/elementRefusal.js).
	const { data } = await api.post(API_SAVED_FILTERS_URL(collectionId), withElementChoicesField(payload, elementChoices));
	return asRead(data);
}

/**
 * Partial update. Only the keys present are touched; `null` clears a stored
 * sort, its further levels (`sort_then`, `[]` too) or grouping period (and for `group_by`, `null` is the ONLY thing that
 * clears one — '' is a 400). `scope` is not patchable — promote/demote below
 * are its own endpoints.
 */
export async function updateSavedFilter(collectionId, filterId, patch = {}) {
	const payload = {};
	['name', 'query', 'sort_field', 'sort_direction'].forEach(key => {
		if (patch[key] !== undefined) payload[key] = patch[key];
	});
	if (patch.group_by !== undefined) payload.group_by = patch.group_by || null;
	// The further sort levels are replaced whole: null or [] clears them (KPR-1636).
	if (patch.sort_then !== undefined) payload.sort_then = sortThenPayload(patch.sort_then);
	// null clears a stored board (KPR-1395).
	if (patch.board !== undefined) payload.board = patch.board?.by ? boardPayload(patch.board) : null;
	// What it opens in (KPR-1641): each replaced whole; null (or none) clears it.
	VIEW_KEYS.forEach((key) => {
		if (patch[key] !== undefined) payload[key] = viewPayload(key, patch[key]);
	});
	const { data } = await api.patch(filterUrl(collectionId, filterId), withElementChoicesField(payload, patch.elementChoices));
	return asRead(data);
}

export async function deleteSavedFilter(collectionId, filterId) {
	await api.delete(filterUrl(collectionId, filterId));
}

/** manage — moves the filter into the collection tier (creator kept as provenance). */
export async function promoteSavedFilter(collectionId, filterId) {
	const { data } = await api.post(`${filterUrl(collectionId, filterId)}/promote`);
	return data;
}

/** manage — pulls it back to the user tier, owned by the CALLER. */
export async function demoteSavedFilter(collectionId, filterId) {
	const { data } = await api.post(`${filterUrl(collectionId, filterId)}/demote`);
	return data;
}
